// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useStepsData } from '@shared/ui-kit';
import { useBackNavigationGuard } from './use-back-navigation-guard';

export interface NavigationControlsProps {
  nextIcon?: ReactNode;
  nextLabel?: ReactNode;
  nextDisabled?: boolean;
  onNext?: () => boolean | void | Promise<boolean | void>;
  onPrevious?: () => void;
  form?: string;
  isFormValid?: () => boolean;
  loading?: boolean;
  loadingLabel?: ReactNode;
  hidePrevious?: boolean;
}

export const useNavigationControls = (props: NavigationControlsProps) => {
  const { isFirstStep, isLastStep, next, prev } = useStepsData();
  const { canGoBack } = useBackNavigationGuard(isFirstStep);

  const onNext = useCallback(() => {
    const run = async () => {
      const result = props?.onNext ? await props.onNext() : undefined;

      if (result !== false) {
        next();
      }
    };

    void run();
  }, [next, props]);

  const onPrevious = useCallback(() => {
    if (!canGoBack) {
      return;
    }

    if (props?.onPrevious) {
      props.onPrevious();
    }

    prev();
  }, [canGoBack, prev, props]);

  const isNextDisabled = useMemo(() => {
    if (props?.isFormValid) {
      return !props.isFormValid();
    }

    return props.nextDisabled ?? isLastStep;
  }, [props, isLastStep]);

  const isPreviousDisabled = !canGoBack;

  return {
    isFirstStep,
    isLastStep,
    onNext,
    onPrevious,
    isNextDisabled,
    isPreviousDisabled,
    hidePrevious: props?.hidePrevious ?? false,
  };
};
